import { useEffect, useLayoutEffect, useMemo } from "react"

import { datadogRum } from "@datadog/browser-rum"
import Intercom from "@intercom/messenger-js-sdk"
import { UserbackProvider, useUserback } from "@userback/react"
import { App as AntdApp, ConfigProvider, Layout, notification, Spin, Typography } from "antd"
import enGB from "antd/locale/en_GB"
import cls from "classname"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useResizeDetector } from "react-resize-detector"
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { compose } from "redux"

import { HelpModal } from "../../Components/Modal/HelpModal"
import PrivateRoute from "../../Components/PrivateRoute"
import Spinner from "../../Components/Spinner"
import { RoutesPaths } from "../../constants/user-routes"
import withAuth from "../../hoc/withAuth"
// HOC
import useWindowSize from "../../hooks/use-window-size"
import { useAttributionTracking } from "../../hooks/useAttributionTracking"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { useStartupRedirect } from "../../hooks/useStartupRedirect"
import i18n from "../../i18n"
import { navActions } from "../../Redux/nav/nav.slice"
import { ErrorsActions } from "../../Redux/redux/errors.slice"
import { darkTheme, lightTheme } from "../../theme"
import { defaultInnerPage } from "../../Utils/appRoutes"
import { loadInvalidTokenState, saveInvalidTokenState } from "../../Utils/invalidTokenStorage"
import {
  createAccountStepPath,
  dateFormat,
  excludeReferences,
  useBackListener
} from "../../Utils/utils"
import AddCard from "../AddCard"
import AdminAcceptInvitePage from "../AdminAcceptInvitePage"
import AdminTwoFactorSetupPage from "../AdminTwoFactorSetupPage"
import AdminTwoFactorVerificationPage from "../AdminTwoFactorVerificationPage"
import { getStatisticsAdmin } from "../BusinessAccountsPage/redux/businessAccounts.thunks"
import CheckEmailCode from "../CheckEmailCode"
import CheckMailboxPage from "../CheckMailboxPage"
import LayoutOnboarding from "../LayoutOnboarding/LayoutOnboarding"
import LoginPage from "../LoginPage"
import { refreshTokenFailure } from "../LoginPage/redux/auth.actions"
import { authActions } from "../LoginPage/redux/auth.slice"
import { logoutThunk, refreshToken, signIn, verifyToken } from "../LoginPage/redux/auth.thunks"
import MCPConsentPage from "../MCPConsentPage"
import NewPasswordAdminPage from "../NewPasswordAdminPage"
import NewPasswordPage from "../NewPasswordPage"
import OAuthAuthPage from "../OAuthAuthPage"
import OnboardingAlertsPageNew from "../Onboarding/AlertsPage"
import OnboardingBusinessInfoPageNew from "../Onboarding/BusinessInfoPage"
import OnboardingCardInfoPageNew from "../Onboarding/CardInfoPage"
import OnboardingEnrollmentPageNew from "../Onboarding/EnrollmentPage"
import OnboardingPaymentConnectPageNew from "../Onboarding/PaymentConnectPage"
import OnboardingTopUp from "../Onboarding/TopUp"
import OnboardingAlertsPage from "../OnboardingAlertsPage"
import OnboardingPaymentConnectPage from "../OnboardingPaymentConnectPage"
import OnboardingQuestionsPage from "../OnboardingQuestionsPage"
import McpDocPage from "../PersonalSettings/Mcp/mcp-doc"
import PremiumFeaturesPage from "../PremiumFeaturesPage"
import RestorePasswordPage from "../RestorePasswordPage"
import ShopifyAuthPage from "../ShopifyAuthPage"
import ShopifyMoveModals from "../ShopifyMove/components/ShopifyMoveModals"
import SignUpPage from "../SignUpPage"
import StartupPage from "../StartupPage"
import StripeAuthPage from "../StripeAuthPage"
import { useFetchUserPreferences } from "../User/hooks/useFetchUserPreferences"
// REDUCERS
import { changeNextCreateAccountStep } from "../User/redux/user.actions"
import { userActions } from "../User/redux/user.slice"
import {
  getBusinessAccount,
  getBusinessAccounts,
  getBusinessAccountsWithRole,
  getEnrollments,
  getPaymentProcessors
} from "../User/redux/user.thunks"
import VerifyCodePage from "../VerifyCodePage"
import InternalPages from "./InternalPages"

const APP_TYPE = process.env.REACT_APP_TYPE
const IS_PROD = process.env.REACT_APP_ENVIRONMENT === "prod"

const myTheme = { light: lightTheme, dark: darkTheme }

const { Text } = Typography

dayjs.extend(utc)

const updateDataDogUser = (userInfo, isAdmin) => {
  if (!userInfo) return

  datadogRum.setUser({
    id: userInfo.id,
    name: userInfo.first_name,
    email: userInfo.email,
    current_business_account_id: userInfo.current_business_account_id,
    business_account_role: userInfo.ba_role?.name || userInfo.ba_role?.id || "No role",
    business_account_role_id: userInfo.ba_role?.id,
    is_admin: isAdmin,
    onboarding_finished: userInfo?.new_onboarding_finished || userInfo?.onboarding_finished
  })
}

// UserbackProvider is now always mounted (see below for why), but paired with
// `delayInit` so it stays a no-op React context on mobile - the actual Userback
// script only loads via init() the first time the user is on desktop, matching the
// original mobile-skips-Userback behavior with zero extra network/JS cost on mobile.
const UserbackVisibility = ({ isMobile }) => {
  const userback = useUserback()

  useEffect(() => {
    if (isMobile) {
      if (userback?.isLoaded?.()) userback.hideLauncher()
      return
    }
    if (userback?.isLoaded?.()) {
      userback.showLauncher()
    } else {
      userback?.init?.()
    }
  }, [isMobile, userback])

  return null
}

//const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID
function Routess(props) {
  const {
    auth,
    theme,
    navSetProp,
    userInfo,
    isAdmin,
    isMobile,
    error,
    verifyToken,
    businessAccount,
    nextCreateAccountStep,
    redirectAfterCreateAccount,
    test_data
    //isPasswordCreated,
  } = props
  const { t } = useTranslation()

  const configNotification = {
    placement: isMobile ? "topRight" : "bottomRight",
    getContainer: () => document.getElementById("main-layout")
    //duration: 0,
  }

  const [apiNotification, contextHolder] = notification.useNotification(configNotification)
  const { width, ref } = useResizeDetector()
  const [, innerHeight] = useWindowSize()
  const location = useLocation()
  const navigate = useNavigate()
  const externalDemoMode = useExternalDemoMode()
  const search = location.search
  const stripe = useMemo(
    () => props.paymentProcessors.find((p) => p.service_name === "stripe"),
    [props.paymentProcessors]
  )
  const invalid = !!stripe?.has_invalid_token
  const [searchParams, setSearchParams] = useSearchParams()
  const inviteId = searchParams.get("invite")

  let isAuthUserPages = !!auth?.accessToken && !isAdmin
  let isAuthUserOnboardingPages =
    !!auth?.accessToken && !isAdmin && !userInfo?.is_created_by_invitation
  const onboarding_finish_notification = window.localStorage.getItem(
    "onboarding_finish_notification"
  )
  const isOnboardingPageAllowed =
    !userInfo?.new_onboarding_finished || onboarding_finish_notification === "true"
  const isVerifiedEmailOnboardingAllowed =
    isAuthUserOnboardingPages && isOnboardingPageAllowed && !!userInfo?.is_email_verified

  const isInvitedWithoutRole = userInfo?.is_created_by_invitation && !!userInfo?.role

  const setHelpModal = () => {
    if (isAdmin) return
    const params = new URLSearchParams(location.search)
    params.set("help_modal", "stripe_token_invalid")

    navigate({
      pathname: location.pathname,
      search: params.toString()
    })
  }

  // STARTUP
  const startupReady = useStartupRedirect()
  useFetchUserPreferences()

  useEffect(() => {
    if (externalDemoMode) {
      if (!auth?.accessToken) {
        props.signIn({ email: "demo_mode@example.com", password: "12345678Xvz" })
      }
      if (theme === "dark") {
        props.dispatch(navActions.navSetProp({ key: "theme", value: "light" }))
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  useEffect(() => {
    const state = loadInvalidTokenState()
    const dismissed = !!state.dismissed
    const shown = !!state.shown

    if (!invalid) {
      if (shown || dismissed) saveInvalidTokenState({ shown: false, dismissed: false })
      return
    }

    if (shown || dismissed) return

    saveInvalidTokenState({ shown: true, dismissed: false })
    setHelpModal(true)
  }, [invalid])

  // for clear charts custom tooltip element from body of page (because it can not remove on other pages automatically)
  useEffect(() => {
    const element = document.getElementById("chartjs-tooltip")
    if (element) element?.remove()
    if (!isAdmin && userInfo) window?.Intercom?.("update")
  }, [location, isAdmin, userInfo])

  useEffect(() => {
    updateDataDogUser(userInfo, isAdmin)
    if (userInfo) datadogRum?.getUser()
  }, [userInfo, isAdmin])

  useEffect(() => {
    if (isAdmin) {
      props.getStatistics({ test_data })
    }
  }, [isAdmin, location.pathname, test_data])

  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      .setAttribute("content", theme === "dark" ? "#030625" : "#ffffff")
    document.body.style.backgroundColor = theme === "dark" ? "#030625" : "#ffffff"
  }, [theme])

  useEffect(() => {
    if (innerHeight) document.documentElement.style.setProperty("--app-height", `${innerHeight}px`)
  }, [innerHeight])

  // REFRESH TOKEN
  useEffect(() => {
    // if (auth?.accessToken) verifyToken(auth.accessToken)
    if (userInfo && !isAdmin) {
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        user_id: userInfo?.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
        name: userInfo?.first_name + " " + userInfo?.first_name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
        email: userInfo?.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
        created_at: dayjs(userInfo?.created).unix(), // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
        hide_default_launcher: false
      })
      if (userInfo?.["current_business_account_id"]) {
        const currentBusinessAccountId = userInfo?.["current_business_account_id"]

        if (nextCreateAccountStep && location.pathname?.includes("create-account")) {
          if (businessAccount?.id !== currentBusinessAccountId) {
            props.getBusinessAccount(currentBusinessAccountId)
          }
          if (!props.enrollments.length) props.getEnrollments(currentBusinessAccountId)
        } else {
          if (location.pathname?.includes("onboarding") || location.pathname === "/login") {
            if (businessAccount?.id !== currentBusinessAccountId && !inviteId) {
              props.getBusinessAccount(currentBusinessAccountId)
            }
            if (!props.enrollments.length) props.getEnrollments(currentBusinessAccountId)
            if (!props.paymentProcessors.length)
              props.getPaymentProcessors({
                merchant_id: currentBusinessAccountId,
                return_url: process.env.REACT_APP_BASE_URI + location.pathname
              })
          }
        }
      }
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [auth?.accessToken, auth?.refreshToken, userInfo?.id, isAdmin])

  useEffect(() => {
    if (userInfo && userInfo?.["current_business_account_id"]) {
      if (location.pathname?.includes("onboarding")) {
        if (businessAccount?.id !== userInfo?.["current_business_account_id"] && !inviteId)
          props.getBusinessAccount(userInfo?.["current_business_account_id"])
      }
    }
  }, [
    auth?.accessToken,
    auth?.refreshToken,
    userInfo?.["current_business_account_id"],
    location.pathname
  ])

  const user_data = useMemo(
    () => ({
      id: userInfo?.id,
      info: {
        name: userInfo?.first_name,
        email: userInfo?.email
      }
    }),
    [userInfo?.id]
  )

  // ERRORS
  useEffect(() => {
    if (error) {
      if (error?.message) {
        if (typeof error.message === "object") {
          for (const key in error.message) {
            //notification(`${key}: ${error.message[i]}`, `error_${key}`)
            if (key === "type") continue //if changed password there are more keys
            if (key === "detail" && typeof error.message[key] !== "object") {
              apiNotification.error({
                closeIcon: <Text>{t("Dismiss")}</Text>,
                message: error?.message[key],
                key: `error_${key}`
              })
            } else {
              apiNotification.error({
                closeIcon: <Text>{t("Dismiss")}</Text>,
                message: "Some back error, see response"
              })
            }
          }
        } else {
          apiNotification.error({
            closeIcon: <Text>{t("Dismiss")}</Text>,
            message: error?.message,
            key: "error notification"
          })
        }
      } else if (typeof error === "string") {
        apiNotification.error({
          closeIcon: <Text>{t("Dismiss")}</Text>,
          message: error,
          key: "error notification"
        })
      } else {
        for (const i in error) {
          if (i === "detail" && typeof error[i] !== "object") {
            apiNotification.error({
              closeIcon: <Text>{t("Dismiss")}</Text>,
              description: error[i],
              key: `error_${i}`
            })
          } else {
            apiNotification.error({
              closeIcon: <Text>{t("Dismiss")}</Text>,
              message: i,
              description: error[i],
              key: `error_${i}`
            })
          }
        }
      }
      props.errorReset()
    }
  }, [!!error])

  useEffect(() => {
    if (auth?.message) {
      apiNotification.success({
        closeIcon: <Text>{t("Dismiss")}</Text>,
        message: auth?.message,
        key: "success notification"
      })
      props.clearAuthMessage()
    }
  }, [auth?.message])

  // RESIZE
  useEffect(() => {
    const correctWidth = width || window.innerWidth
    if (correctWidth > 768) {
      navSetProp("isDesktop", true)
      navSetProp("isMobile", false)
    } else {
      navSetProp("isDesktop", false)
      navSetProp("isMobile", true)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [width, !!userInfo])

  // SET LANGUAGE
  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || "en")
    dayjs.locale(userInfo?.language === "zh-hans" ? "zh-cn" : userInfo?.language || "en")
  }, [userInfo?.language])

  useBackListener(({ location: backLocation }) => {
    if (
      excludeReferences.includes(backLocation.pathname) &&
      !excludeReferences.includes(location.pathname)
    ) {
      props.userSaveResultReset()
      props.getBusinessAccounts()
      navigate(createAccountStepPath[1] + (search || ""), { replace: true })
    } else if (
      excludeReferences.includes(backLocation.pathname) &&
      excludeReferences.includes(location?.pathname) &&
      !nextCreateAccountStep
    ) {
      navigate(defaultInnerPage, { replace: true })
    }
  })

  useLayoutEffect(() => {
    if (userInfo?.next_step >= 4 && !!userInfo && !isAdmin) {
      props.getBusinessAccountsWithRole()
      props.getBusinessAccounts()
    }
    if (isInvitedWithoutRole) {
      props.getBusinessAccounts()
    }
  }, [])

  // Attribution tracking
  useAttributionTracking()

  if (!startupReady) {
    return (
      <Layout>
        <Routes>
          <Route path="*" element={<StartupPage />} />
        </Routes>
      </Layout>
    )
  }

  Spin.setDefaultIndicator(<Spinner />)

  const appContent = (
    <ConfigProvider locale={enGB} dateFormat={dateFormat} theme={{ ...myTheme[theme] }}>
      <AntdApp notification={configNotification}>
        <Layout
          id="main-layout"
          ref={ref}
          className={cls("main-layout", {
            "theme-light": theme === "light",
            "theme-dark": theme === "dark"
          })}
        >
          {contextHolder}
          {APP_TYPE === "partner" ? (
            <Routes>
              <Route path="/" element={<SignUpPage isReferralOutsider />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/check-mailbox" element={<CheckMailboxPage />} />
              <Route path="/restore-password" element={<RestorePasswordPage />} />
              <Route path="/restore-password/:hash" element={<NewPasswordPage />} />
              <Route path="/restore-password/setting/:hash" element={<NewPasswordPage />} />
              <Route path="/verify-code" element={<VerifyCodePage />} />
              <Route path="/sign-up/confirm/:hash" element={<CheckEmailCode />} />
              <Route path="*" element={<InternalPages />} />
              <Route
                path="/onboarding/questions"
                element={
                  <PrivateRoute redirectPath={"/referrals"} auth={isAuthUserOnboardingPages}>
                    <OnboardingQuestionsPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<StartupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/:hash" element={<LoginPage />} />
              <Route
                path="/admin/2fa-verification/:token"
                element={<AdminTwoFactorVerificationPage />}
              />
              <Route path="/admin/2fa-setup/:token" element={<AdminTwoFactorSetupPage />} />
              <Route path="/admin-invite/:token" element={<AdminAcceptInvitePage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/sign-up/:hash" element={<SignUpPage />} />
              <Route path="/add-email" element={<SignUpPage />} />
              <Route path="/auth/shopify/" element={<ShopifyAuthPage />} />
              <Route path="/auth/stripe/" element={<StripeAuthPage />} />
              <Route
                path="/auth/nextcommerce/"
                element={<OAuthAuthPage provider="nextcommerce" />}
              />
              <Route path="/oauth/mcp/authorize" element={<MCPConsentPage />} />
              <Route path="/check-mailbox" element={<CheckMailboxPage />} />
              {/*_ONBOARDING PAGES_____START_*/}
              {/* <Route path="/onboarding/questions" element={
                <PrivateRoute redirectPath={RoutesPaths.dashboard.root} auth={isAuthUserOnboardingPages}>
                  <OnboardingQuestionsPage/>
                </PrivateRoute>
              }/> //step 2.5 */}
              <Route element={<LayoutOnboarding />}>
                <Route
                  path="/onboarding/business-info"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingBusinessInfoPageNew step={1} maxStep={5} />
                      {/* <OnboardingBusinessInfoPage step={1} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/payment-connect"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingPaymentConnectPageNew step={2} maxStep={5} />
                      {/* <OnboardingPaymentConnectPage step={2} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/enrollment"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingEnrollmentPageNew step={3} maxStep={5} />
                      {/* <OnboardingEnrollmentPage step={3} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/alerts"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingAlertsPageNew step={4} maxStep={5} />
                      {/* <OnboardingAlertsPage step={4} maxStep={5}/> */}
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/card-info/:status/"
                  element={
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/card-info"
                  element={
                    // <PrivateRoute redirectPath={RoutesPaths.dashboard.root} auth={isAuthUserOnboardingPages}>
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/onboarding/top-up"
                  element={
                    // <PrivateRoute redirectPath={RoutesPaths.dashboard.root} auth={isAuthUserOnboardingPages}>
                    <PrivateRoute
                      redirectPath={"/getting_started"}
                      auth={isVerifiedEmailOnboardingAllowed}
                    >
                      <OnboardingTopUp step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
              </Route>
              {/*_ONBOARDING PAGES_____END_*/}
              {/*_CREATE BUSINESS ACCOUNT PAGES_____START_*/}
              <Route element={<LayoutOnboarding />}>
                <Route
                  path="/create-account/business-info"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingBusinessInfoPage step={1} maxStep={5}/> */}
                      <OnboardingBusinessInfoPageNew step={1} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/payment-connect"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingPaymentConnectPage step={2} maxStep={5}/> */}
                      <OnboardingPaymentConnectPageNew step={2} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/enrollment"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingEnrollmentPage step={3} maxStep={5}/> */}
                      <OnboardingEnrollmentPageNew step={3} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/alerts"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingAlertsPage step={4} maxStep={5}/> */}
                      <OnboardingAlertsPageNew step={4} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/card-info/:status/"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/card-info"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingCardInfoPageNew step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-account/top-up"
                  element={
                    <PrivateRoute
                      redirectPath={redirectAfterCreateAccount || RoutesPaths.dashboard.root}
                      auth={nextCreateAccountStep}
                    >
                      {/* <OnboardingCardInfoPage step={5} maxStep={5}/> */}
                      <OnboardingTopUp step={5} maxStep={5} />
                    </PrivateRoute>
                  }
                />
              </Route>
              {/*_CREATE BUSINESS PAGES_____END_*/}
              {/*_MANAGE ENROLLMENT PAGES_____START_*/}
              <Route
                path={`${RoutesPaths.configure.enrollment}/:enrollmentId/payment-connect`}
                element={<OnboardingPaymentConnectPage step={1} maxStep={2} />}
              />
              <Route
                path={`${RoutesPaths.configure.enrollment}/:enrollmentId/alerts`}
                element={<OnboardingAlertsPage step={2} maxStep={2} />}
              />
              <Route
                path="/business_settings/enrollment/:enrollmentId/payment-connect"
                element={<OnboardingPaymentConnectPage step={1} maxStep={2} />}
              />
              <Route
                path="/business_settings/enrollment/:enrollmentId/alerts"
                element={<OnboardingAlertsPage step={2} maxStep={2} />}
              />
              {/*_MANAGE ENROLLMENT PAGES_____END_*/}
              <Route path="/sign-up/confirm/:hash" element={<CheckEmailCode />} />
              <Route path="/success/:hash" element={<CheckEmailCode />} />
              <Route path="/api/success/:hash" element={<CheckEmailCode />} />
              <Route path="/restore-password" element={<RestorePasswordPage />} />
              <Route path="/verify-code" element={<VerifyCodePage />} />
              <Route path="/restore-password/:hash" element={<NewPasswordPage />} />
              <Route path="/restore-password/setting/:hash" element={<NewPasswordPage />} />
              <Route path="/create-new-password" element={<NewPasswordAdminPage />} />
              <Route
                path={RoutesPaths.prevent.premium_features}
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <PremiumFeaturesPage />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<InternalPages />} />
              <Route
                path="/add-card"
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <AddCard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/manual-top-up"
                element={
                  <PrivateRoute redirectPath={"/login"} auth={isAuthUserPages}>
                    <AddCard />
                  </PrivateRoute>
                }
              />
              <Route path="mcp" element={<McpDocPage />} />
            </Routes>
          )}
          <HelpModal />
          {/* Mounted app-wide: the Shopify move offer is resolved on the OAuth
              return page, but the popups must survive wherever the user ends up. */}
          <ShopifyMoveModals />
        </Layout>
      </AntdApp>
    </ConfigProvider>
  )

  // Only mounted in prod - Userback must never load its script or send requests
  // in non-prod environments.
  if (!IS_PROD) return appContent

  // Always mounted (never conditionally added/removed based on isMobile) - see
  // UserbackVisibility above for why. Mobile just hides the launcher instead.
  return (
    <UserbackProvider token="A-hM9QRGQUnjNFvEdLoVb8XV1CN" options={{ user_data }} delayInit>
      <UserbackVisibility isMobile={isMobile} />
      {appContent}
    </UserbackProvider>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  theme: state.nav.theme,
  userInfo: state.auth.userInfo,
  isPasswordCreated: state.auth.isPasswordCreated,
  isDesktop: state.nav.isDesktop,
  isMobile: state.nav.isMobile,
  isAdmin: state.auth.isAdmin,
  error: state.errors.data,

  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount,
  enrollments: state.user.enrollments,
  paymentProcessors: state.user.paymentProcessors,
  nextCreateAccountStep: state.user.nextCreateAccountStep,
  redirectAfterCreateAccount: state.user.redirectAfterCreateAccount,

  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  navSetProp: (key, value) => dispatch(navActions.navSetProp({ key, value })),
  errorReset: () => dispatch(ErrorsActions.errorReset()),
  errorSave: (data) => dispatch(ErrorsActions.errorSave(data)),
  refreshToken: (token) => dispatch(refreshToken({ token })),
  refreshTokenFailure: (data) => dispatch(refreshTokenFailure(data)),
  logout: () => dispatch(logoutThunk()),
  signIn: (data) => dispatch(signIn(data)),
  verifyToken: (token) => dispatch(verifyToken({ token })),
  clearAuthMessage: () => dispatch(authActions.clearAuthMessage()),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),
  changeNextCreateAccountStep: (nextStep) => dispatch(changeNextCreateAccountStep(nextStep)),
  getBusinessAccounts: () => dispatch(getBusinessAccounts()),
  getBusinessAccount: (businessAccountId) => dispatch(getBusinessAccount(businessAccountId)),
  getEnrollments: (businessAccountId) => dispatch(getEnrollments(businessAccountId)),
  getPaymentProcessors: (data) => dispatch(getPaymentProcessors(data)),
  getBusinessAccountsWithRole: () => dispatch(getBusinessAccountsWithRole()),

  getStatistics: (params) => dispatch(getStatisticsAdmin(params)),
  dispatch
})

Routes.propTypes = {
  auth: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    isValidRefreshToken: PropTypes.bool
  }),
  userInfo: PropTypes.shape({
    onboarding_finished: PropTypes.bool,
    language: PropTypes.string
  }),
  isDesktop: PropTypes.bool,
  isMobile: PropTypes.bool,
  isAdmin: PropTypes.bool,
  error: PropTypes.any,
  dispatch: PropTypes.func
}

export default connect(mapStateToProps, mapDispatchToProps)(compose(withAuth)(Routess))
